import React, { createContext, useContext, useState, useEffect } from 'react';
import { ThemeProvider as MUIThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { PaletteMode } from '@mui/material';
import { lightTheme, darkTheme } from './themesV3';
import { CustomTheme } from './themeTypes';

type ThemeContextType = {
  mode: PaletteMode;
  theme: CustomTheme;
  toggleColorMode: () => void;
};

const ThemeContext = createContext<ThemeContextType>({
  mode: 'light',
  theme: lightTheme,
  toggleColorMode: () => {},
});

export const useThemeContext = () => useContext(ThemeContext);

// Custom hook for easy access to theme colors
export const useThemeColors = () => {
  const { theme } = useThemeContext();
  return theme.palette.custom;
};

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Try to get the theme from localStorage, default to system preference or 'light'
  const [mode, setMode] = useState<PaletteMode>(() => {
    // Check localStorage first
    const savedMode = localStorage.getItem('theme-mode');
    if (savedMode === 'light' || savedMode === 'dark') {
      return savedMode;
    }
    
    // Then check system preference
    if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      return 'dark';
    }
    
    // Default to light
    return 'light';
  });

  // Listen for system theme changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const handleChange = (e: MediaQueryListEvent) => {
      // Only update if the user hasn't set a preference
      if (!localStorage.getItem('theme-mode')) {
        setMode(e.matches ? 'dark' : 'light');
      }
    };
    
    // Add listener
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
    } else {
      // Fallback for older browsers
      mediaQuery.addListener(handleChange);
    }
    
    // Cleanup
    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', handleChange);
      } else {
        // Fallback for older browsers
        mediaQuery.removeListener(handleChange);
      }
    };
  }, []);

  // Use the direct themes from themesV3
  const theme = mode === 'dark' ? darkTheme : lightTheme;

  const toggleColorMode = () => {
    setMode((prevMode) => {
      const newMode = prevMode === 'light' ? 'dark' : 'light';
      localStorage.setItem('theme-mode', newMode);
      
      // Force a complete rerender to ensure clean theme application
      setTimeout(() => {
        window.dispatchEvent(new Event('resize'));
      }, 0);
      
      return newMode;
    });
  };

  const contextValue: ThemeContextType = {
    mode,
    theme,
    toggleColorMode,
  };

  return (
    <ThemeContext.Provider value={contextValue}>
      <MUIThemeProvider theme={theme}>
        <CssBaseline /> {/* This applies the theme's background and resets CSS */}
        {children}
      </MUIThemeProvider>
    </ThemeContext.Provider>
  );
};
